const IMAGE_MIMES = [
  "image/jpeg",
  "image/png",
  "image/gif",
  "image/webp",
  "image/svg+xml",
  "image/bmp",
];

const VIDEO_MIMES = [
  "video/mp4",
  "video/webm",
  "video/ogg",
  "video/quicktime",
  "video/x-msvideo",
  "video/x-ms-wmv",
];

const EXTENSION_TO_MIME = {
  ".pdf": "application/pdf",
  ".doc": "application/msword",
  ".docx": "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  ".jpg": "image/jpeg",
  ".jpeg": "image/jpeg",
  ".png": "image/png",
  ".gif": "image/gif",
  ".svg": "image/svg+xml",
  ".webp": "image/webp",
  ".bmp": "image/bmp",
  ".mp4": "video/mp4",
  ".webm": "video/webm",
  ".ogg": "video/ogg",
  ".mov": "video/quicktime",
};

/**
 * Turns an HTML file input `accept` string into a list of allowed MIME types, or null for no restriction.
 */
export function parseAcceptToAllowedMimeTypes(accept) {
  if (accept == null || String(accept).trim() === "" || accept === "*") {
    return null;
  }
  const tokens = accept
    .split(",")
    .map((p) => p.trim().toLowerCase())
    .filter(Boolean);

  const mimes = new Set();

  for (const token of tokens) {
    if (appendWildcardToken(token, mimes)) continue;

    if (token.startsWith(".")) {
      const mime = EXTENSION_TO_MIME[token];
      if (mime) {
        mimes.add(mime);
      }
      continue;
    }

    if (token.includes("/")) {
      mimes.add(token);
    }
  }

  return mimes.size > 0 ? [...mimes] : null;
}

function appendWildcardToken(token, mimes) {
  if (!token.endsWith("/*")) {
    return false;
  }

  const major = token.slice(0, -2);
  if (major === "image") {
    IMAGE_MIMES.forEach((mime) => mimes.add(mime));
    return true;
  }

  if (major === "video") {
    VIDEO_MIMES.forEach((mime) => mimes.add(mime));
    return true;
  }

  return false;
}

function guessMimeFromFilename(name) {
  const idx = name.lastIndexOf(".");
  if (idx === -1) return "";
  const ext = name.slice(idx).toLowerCase();
  return EXTENSION_TO_MIME[ext] || "";
}

function formatMimeHints(allowedTypes) {
  const hints = allowedTypes.map((type) => {
    const [, sub] = type.split("/");
    return sub?.replace(/^x-/, "") || type;
  });
  return [...new Set(hints)].join(", ");
}

/**
 * Builds helper copy from `accept` and size bounds (shown when parent does not pass `helperText`).
 */
export function describeAcceptHelper(accept, minSizeMB = 0, maxSizeMB = 5) {
  const parts =
    accept == null || accept === "*"
      ? ["Any file type"]
      : accept
          .split(",")
          .map((p) => p.trim())
          .filter(Boolean)
          .map((p) => {
            const low = p.toLowerCase();
            if (low === "image/*") return "Images";
            if (low === "video/*") return "Videos";
            if (low.startsWith(".")) return low.slice(1).toUpperCase();
            return p;
          });

  const typeLine = [...new Set(parts)].join(", ");
  let sizeLine = "";

  if (minSizeMB > 0 && Number.isFinite(maxSizeMB)) {
    sizeLine = ` (${minSizeMB}–${maxSizeMB} MB each)`;
  } else if (Number.isFinite(maxSizeMB)) {
    sizeLine = ` (max. ${maxSizeMB} MB each)`;
  } else if (minSizeMB > 0) {
    sizeLine = ` (min. ${minSizeMB} MB each)`;
  }

  return `${typeLine}${sizeLine}.`;
}

/**
 * Validates a single File: MIME (optional list), size bounds, optional exact image dimensions.
 */
export function validateFileTypeWithSize(
  file,
  allowedTypes = null,
  minSizeMB = 0,
  maxSizeMB = Number.POSITIVE_INFINITY,
  dimensions = [],
) {
  return new Promise((resolve) => {
    if (!file) {
      resolve({ isValid: false, message: "No file provided." });
      return;
    }

    if (Array.isArray(allowedTypes) && allowedTypes.length > 0) {
      const effectiveType = file.type || guessMimeFromFilename(file.name);
      const typeOk = Boolean(effectiveType && allowedTypes.includes(effectiveType));
      if (!typeOk) {
        resolve({
          isValid: false,
          message: `Unsupported type. Allowed: ${formatMimeHints(allowedTypes)}.`,
        });
        return;
      }
    }

    const sizeMB = file.size / (1024 * 1024);

    if (sizeMB < minSizeMB) {
      resolve({
        isValid: false,
        message: `File too small — must be at least ${minSizeMB} MB.`,
      });
      return;
    }

    if (Number.isFinite(maxSizeMB) && sizeMB > maxSizeMB) {
      resolve({
        isValid: false,
        message: `File too large — must be at most ${maxSizeMB} MB.`,
      });
      return;
    }

    if (dimensions.length === 2) {
      const [requiredWidth, requiredHeight] = dimensions;

      const mimeForDimensions = file.type || guessMimeFromFilename(file.name);
      const isImageForDimensions =
        typeof mimeForDimensions === "string" && mimeForDimensions.startsWith("image/");
      if (!isImageForDimensions) {
        resolve({
          isValid: false,
          message: "Dimensions check applies only to image files.",
        });
        return;
      }

      const url = URL.createObjectURL(file);
      const img = new Image();

      img.onload = () => {
        URL.revokeObjectURL(url);
        if (img.width === requiredWidth && img.height === requiredHeight) {
          resolve({ isValid: true, message: "Image is valid." });
          return;
        }
        resolve({
          isValid: false,
          message: `Image must be exactly ${requiredWidth}×${requiredHeight} px (got ${img.width}×${img.height}).`,
        });
      };

      img.onerror = () => {
        URL.revokeObjectURL(url);
        resolve({
          isValid: false,
          message: "Could not load image for dimension validation.",
        });
      };

      img.src = url;
      return;
    }

    resolve({ isValid: true, message: "File is valid." });
  });
}
